import React from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import { MapStrikethrough } from '@apigrowfinance/pbolt-icons';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage } from 'react-intl';

import { AddressDetails } from '@clients/accountsClient';
import MapWrapperContainer from '@components/map/MapWrapperContainer';
import MapPanelError from '@components/overviewPanels/mapPanel/MapPanelError';
import useGetTransactions from '@hooks/queries/useGetTransactions';
import { getGoogleMapsAPIKey } from '@utils/URLs';

interface MapPanelProps {
    accountId: string;
}

const MapPanel = ({ accountId }: MapPanelProps) => {
    const { data: transactions, isLoading } = useGetTransactions({
        accountId: +accountId,
        order: 'desc',
    });
    let mostRecentMerchant = null;
    const { theme } = useTheme();

    if (transactions?.pages[0]?.items?.length > 0) {
        let i = 0;
        while (i < transactions?.pages?.length && mostRecentMerchant == null) {
            const mostRecentTransactionWithMerchant = transactions?.pages[
                i
            ]?.items?.find((x) => x.merchant != null);
            if (mostRecentTransactionWithMerchant) {
                mostRecentMerchant = {
                    transactionId: mostRecentTransactionWithMerchant?.id,
                    merchantDetails:
                        mostRecentTransactionWithMerchant?.merchant,
                };
            }
            i++;
        }
    }

    const hasData = !!mostRecentMerchant;

    // TODO handle !merchantHasAddress
    // TODO check transaction order is most recent first
    // TODO translate state
    // TODO if city or state are not present they will say undefined atm
    // TODO Comma should only be present if both city and state are present
    // TODO handle transactionError
    if (getGoogleMapsAPIKey() != '') {
        return (
            <Panel>
                <PanelTitle loading={isLoading} hideArrows>
                    <FormattedMessage
                        id="MapPanel.lastLocated"
                        defaultMessage="Last location"
                    />
                </PanelTitle>
                <PanelContent>
                    <Box
                        sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                        {isLoading ? (
                            <Skeleton
                                width={60}
                                data-testid="location-text-loader"
                            />
                        ) : (
                            <>
                                {hasData ? (
                                    <>
                                        <Typography variant="body2">
                                            {`${mostRecentMerchant?.merchantDetails?.city}, ${mostRecentMerchant?.merchantDetails?.state}`}
                                        </Typography>
                                        <Box width="100%" height="100%">
                                            <MapWrapperContainer
                                                idToCenterOn={mostRecentMerchant?.transactionId?.toString()}
                                                showLabels={false}
                                                disableControls
                                                zoomLevel={9}
                                                isLoading={isLoading}
                                                sxProps={{
                                                    borderRadius:
                                                        theme.shape
                                                            .borderRadius,
                                                    borderStyle: 'solid',
                                                    borderWidth: 1,
                                                    borderColor:
                                                        theme.palette.grey[200],
                                                }}
                                                markerAddresses={[
                                                    {
                                                        id: +mostRecentMerchant?.transactionId,
                                                        city: mostRecentMerchant
                                                            ?.merchantDetails
                                                            ?.city,
                                                        country:
                                                            mostRecentMerchant
                                                                ?.merchantDetails
                                                                ?.state,
                                                    } as AddressDetails,
                                                ]}
                                            />
                                        </Box>
                                    </>
                                ) : (
                                    <MapPanelError
                                        icon={<MapStrikethrough />}
                                        title={
                                            <FormattedMessage
                                                id="MapPanel.noTransactionTitle"
                                                defaultMessage="No map available at this time."
                                            />
                                        }
                                        description={
                                            <FormattedMessage
                                                id="MapPanel.noTransactionDesc"
                                                defaultMessage="Could not find a transaction with location data."
                                            />
                                        }
                                    />
                                )}
                            </>
                        )}
                    </Box>
                </PanelContent>
            </Panel>
        );
    } else {
        return (
            <Panel>
                <PanelContent>
                    <MapPanelError
                        icon={<MapStrikethrough />}
                        title={
                            <FormattedMessage
                                id="MapPanel.title"
                                defaultMessage="No map available at this time."
                            />
                        }
                        description={
                            <FormattedMessage
                                id="MapPanel.desc"
                                defaultMessage="We're working on it, check back soon."
                            />
                        }
                    />
                </PanelContent>
            </Panel>
        );
    }
};

export default MapPanel;
